import { Component } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AddProductDialogComponent } from './add-product-dialog/add-product-dialog.component';
import { DeleteConfirmDialogComponent } from '../delete-confirm-dialog/delete-confirm-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { AdminCommonHttpService } from '../service/admin-common-http.service';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AssignToGroupDialogComponent } from './assign-to-group-dialog/assign-to-group-dialog.component';
import { Product } from '../models/combined-interfaces.model';
import { ProductStore } from '../../../stores/product.store';
import { MatSort } from '@angular/material/sort';

export interface Category {
  category_id: number;
  name: string;
}

@Component({
  selector: 'app-manage-product',
  standalone: true,
  imports: [
    MatTableModule,
    MatTabsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    CommonModule,
    FormsModule,
    MatIconModule,
    MatCheckboxModule,
    MatSort
  ],
  templateUrl: './manage-product.component.html',
  styleUrl: './manage-product.component.css',
  animations: [
    trigger('fadeAnimation', [
      state('in', style({ opacity: 1 })),
      transition(':leave', [animate('300ms ease-out', style({ opacity: 0 }))])
    ])
  ]
})
export class ManageProductComponent {
  // table
  public displayedColumns: string[] = [
    'select',
    'SKU',
    'name',
    'price',
    'groups',
    'sale',
    'actions'
  ];

  // pagination + search
  pageSizes = [10, 25, 50];
  searchText: string = '';

  // selection + categories
  selectedProductId: number | null = null;
  selectedCategoryIds: number[] = [];
  categories: Category[] = [];
  linkSuccess: boolean = false;
  linkedCategories: Category[] = [];
  selectedProductIds: number[] = [];

  private searchTextChanged: Subject<string> = new Subject<string>();

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private commonApiService: AdminCommonHttpService,
    private router: Router,
    public productStore: ProductStore
  ) {
    // debounced search
    this.searchTextChanged
      .pipe(debounceTime(300))
      .subscribe((searchTerm) => {
        this.productStore.setSearch(searchTerm);
      });
  }

  // derived values
  get totalProducts(): number {
    return this.productStore.total();
  }

  get currentPage(): number {
    return this.productStore.currentPage();
  }

  get pageSize(): number {
    return this.productStore.pageSize();
  }

  get totalPages(): number {
    const size = this.pageSize || 1;
    return Math.ceil(this.totalProducts / size);
  }

  async ngOnInit() {
    localStorage.setItem('is_admin', 'true');
    await this.productStore.init();
    await this.loadCategories();
  }

  // ---------- data loading ----------

  async loadCategories(): Promise<void> {
    const response = await this.commonApiService.getAllCategories();
    if (response) {
      this.categories = response;
    }
  }

  // ---------- dialogs ----------

  openAddProductDialog(): void {
    const dialogRef = this.dialog.open(AddProductDialogComponent, {
      width: '600px',
      data: {
        dialogTitle: 'Add Product'
      }
    });

    dialogRef.afterClosed().subscribe(async (newProduct) => {
      if (newProduct) {
        try {
          await this.productStore.addProduct(newProduct);
          this.snackBar.open('Product added successfully', 'Close', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom'
          });
        } catch (error) {
          console.error('Failed to add product:', error);
          this.snackBar.open('Failed to add product', 'Close', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom'
          });
        }
      }
    });
  }

  openEditProductDialog(product: Product, event?: MouseEvent): void {
    // prevent row click navigation
    if (event) {
      event.stopPropagation();
    }

    const dialogRef = this.dialog.open(AddProductDialogComponent, {
      width: '600px',
      data: {
        dialogTitle: 'Edit Product',
        productData: product,
        isEditMode: true
      }
    });

    dialogRef.afterClosed().subscribe(async (updatedProduct) => {
      if (updatedProduct) {
        try {
          await this.productStore.updateProduct(
            product.product_id,
            updatedProduct
          );
          this.snackBar.open('Product updated successfully', 'Close', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom'
          });
        } catch (error) {
          console.error('Failed to update product:', error);
          this.snackBar.open('Failed to update product', 'Close', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom'
          });
        }
      }
    });
  }

  openDeleteProductDialog(product: Product, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: {
        message: `Are you sure you want to delete the product "${product.name}"?`,
        buttonValue: 'Delete'
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        product.deleted = true;

        const snackBarRef = this.snackBar.open('Product deleted', 'Undo', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'bottom'
        });

        let undoClicked = false;

        snackBarRef.onAction().subscribe(() => {
          product.deleted = false;
          undoClicked = true;
        });

        snackBarRef.afterDismissed().subscribe(async () => {
          if (!undoClicked) {
            console.log(
              `Soft deleting product permanently: ${product.name} (ID: ${product.product_id})`
            );
            await this.productStore.deleteProduct(product.product_id);
          }
        });
      }
    });
  }

  async restoreProduct(product: Product): Promise<void> {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: {
        message: `Are you sure you want to restore the product "${product.name}"?`,
        buttonValue: 'Restore'
      }
    });

    dialogRef.afterClosed().subscribe(async (confirmed) => {
      if (confirmed) {
        try {
          await this.productStore.restoreProduct(product.product_id);
          this.snackBar.open('Product restored successfully', 'Close', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom'
          });
        } catch (error) {
          console.error('Failed to restore product:', error);
          this.snackBar.open('Failed to restore product', 'Close', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom'
          });
        }
      }
    });
  }

  // ---------- row navigation ----------

  goToProductDetail(product: Product): void {
    this.router.navigate(
      ['/admin/manage-products', product.product_id],
      { queryParams: { productName: product.name } }
    );
  }

  // ---------- sorting / paging / search ----------

  async onSort(column: string): Promise<void> {
    await this.productStore.sortBy(column);
  }

  async onPageSizeChange(newSize: number): Promise<void> {
    await this.productStore.setPageSize(newSize);
  }

  async prevPage(): Promise<void> {
    if (this.searchText.trim()) return;
    await this.productStore.prevPage();
  }

  async nextPage(): Promise<void> {
    if (this.searchText.trim()) return;
    await this.productStore.nextPage();
  }

  onSearchTextChange(): void {
    this.searchTextChanged.next(this.searchText);
  }

  // ---------- category linking ----------

  onProductSelectionChange(): void {
    this.loadLinkedCategories();
  }

  async linkProductToCategories(): Promise<void> {
    if (!this.selectedProductId || this.selectedCategoryIds.length === 0) {
      return;
    }

    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: {
        message:
          'Are you sure you want to link this product to the selected categories?',
        buttonValue: 'Link'
      }
    });

    const confirmed = await dialogRef.afterClosed().toPromise();

    if (confirmed) {
      const payload = {
        product_id: this.selectedProductId,
        category_ids: this.selectedCategoryIds
      };

      try {
        const response =
          await this.commonApiService.linkProductToCategories(payload);
        if (response) {
          this.linkSuccess = true;
          setTimeout(() => (this.linkSuccess = false), 3000);
        }
      } catch (error) {
        console.error('Failed to link product to categories:', error);
      }
    }
  }

  async loadLinkedCategories(): Promise<void> {
    if (!this.selectedProductId) {
      this.linkedCategories = [];
      return;
    }

    const response =
      await this.commonApiService.getCategoriesForProduct(this.selectedProductId);
    if (response) {
      this.linkedCategories = response;
    }
  }

  async confirmUnlinkCategory(categoryId: number): Promise<void> {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: {
        message:
          'Are you sure you want to unlink this category from the product?',
        buttonValue: 'Unlink'
      }
    });

    const confirmed = await dialogRef.afterClosed().toPromise();

    if (confirmed && this.selectedProductId) {
      try {
        const payload = {
          product_id: this.selectedProductId,
          category_ids: [categoryId],
          tenant_id: localStorage.getItem('admin_tenant_id')
        };
        await this.commonApiService.unlinkProductFromCategories(payload);
        await this.loadLinkedCategories();
      } catch (error) {
        console.error('Failed to unlink category:', error);
      }
    }
  }

  // ---------- multi-select + groups ----------

  toggleProductSelection(productId: number): void {
    const idx = this.selectedProductIds.indexOf(productId);
    if (idx > -1) {
      this.selectedProductIds.splice(idx, 1);
    } else {
      this.selectedProductIds.push(productId);
    }
  }

  toggleSelectAll(event: any): void {
    const current = this.productStore.filteredProducts();
    if (event.checked) {
      this.selectedProductIds = current.map((p) => p.product_id);
    } else {
      this.selectedProductIds = [];
    }
  }

  isAllSelected(): boolean {
    const current = this.productStore.filteredProducts();
    return (
      current.length > 0 &&
      current.every((p) => this.selectedProductIds.includes(p.product_id))
    );
  }

  openAssignToGroupDialog(): void {
    const dialogRef = this.dialog.open(AssignToGroupDialogComponent, {
      width: '400px',
      data: { productIds: this.selectedProductIds }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.snackBar.open('Group assignment completed', 'Close', {
          duration: 3000
        });
        this.selectedProductIds = [];
      }
    });
  }

  getGroupNames(groups?: { id: number; name: string }[] | null): string {
    return Array.isArray(groups) && groups.length
      ? groups.map((g) => g.name).join(', ')
      : '—';
  }
}
